/**
 * Custom React Hook for Medication Orders API
 */

import { useState, useEffect, useCallback } from 'react';
import medicationService from '../services/medicationService';
import {
  MedicationOrderDTO,
  CreateMedicationOrderRequest,
  MedicationOrderStatus,
  PageResponse,
} from '../types/medication.types';

interface UseMedicationsProps {
  residentId?: string;
  autoFetch?: boolean;
}

export const useMedications = ({ residentId, autoFetch = true }: UseMedicationsProps = {}) => {
  const [medications, setMedications] = useState<MedicationOrderDTO[]>([]);
  const [currentMedication, setCurrentMedication] = useState<MedicationOrderDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = 20;

  // Fetch all medication orders with pagination
  const fetchMedications = useCallback(async (page: number = 0) => {
    setLoading(true);
    setError(null);
    try {
      const response: PageResponse<MedicationOrderDTO> = await medicationService.getAllMedicationOrders(page, pageSize);
      setMedications(response.content);
      setTotalElements(response.totalElements);
      setTotalPages(response.totalPages);
      setCurrentPage(response.number);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch medication orders');
      console.error('Error fetching medications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch medication orders by resident
  const fetchMedicationsByResident = useCallback(async (resId: string) => {
    setLoading(true);
    setError(null);
    try {
      const meds = await medicationService.getMedicationOrdersByResident(resId);
      setMedications(meds);
      setTotalElements(meds.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch medication orders for resident');
      console.error('Error fetching medications by resident:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch active medication orders by resident
  const fetchActiveMedicationsByResident = useCallback(async (resId: string) => {
    setLoading(true);
    setError(null);
    try {
      const meds = await medicationService.getActiveMedicationOrdersByResident(resId);
      setMedications(meds);
      setTotalElements(meds.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch active medications for resident');
      console.error('Error fetching active medications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch medication orders by status
  const fetchMedicationsByStatus = useCallback(async (status: MedicationOrderStatus) => {
    setLoading(true);
    setError(null);
    try {
      const meds = await medicationService.getMedicationOrdersByStatus(status);
      setMedications(meds);
      setTotalElements(meds.length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch medications by status');
      console.error('Error fetching medications by status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch medication order by ID
  const fetchMedicationById = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const med = await medicationService.getMedicationOrderById(id);
      setCurrentMedication(med);
      return med;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch medication order');
      console.error('Error fetching medication:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Create new medication order
  const createMedication = useCallback(async (request: CreateMedicationOrderRequest) => {
    setSaving(true);
    setError(null);
    try {
      const newMed = await medicationService.createMedicationOrder(request);
      setMedications((prev) => [newMed, ...prev]);
      setCurrentMedication(newMed);
      return newMed;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create medication order');
      console.error('Error creating medication:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  // Update medication order
  const updateMedication = useCallback(async (id: string, data: Partial<MedicationOrderDTO>) => {
    setSaving(true);
    setError(null);
    try {
      const updatedMed = await medicationService.updateMedicationOrder(id, data);
      setMedications((prev) =>
        prev.map((med) => (med.id === id ? updatedMed : med))
      );
      if (currentMedication?.id === id) {
        setCurrentMedication(updatedMed);
      }
      return updatedMed;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update medication order');
      console.error('Error updating medication:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentMedication]);

  // Approve medication order
  const approveMedication = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      const approvedMed = await medicationService.approveMedicationOrder(id);
      setMedications((prev) =>
        prev.map((med) => (med.id === id ? approvedMed : med))
      );
      if (currentMedication?.id === id) {
        setCurrentMedication(approvedMed);
      }
      return approvedMed;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve medication order');
      console.error('Error approving medication:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentMedication]);

  // Discontinue medication order
  const discontinueMedication = useCallback(async (id: string, reason: string) => {
    setSaving(true);
    setError(null);
    try {
      const discontinuedMed = await medicationService.discontinueMedicationOrder(id, reason);
      setMedications((prev) =>
        prev.map((med) => (med.id === id ? discontinuedMed : med))
      );
      if (currentMedication?.id === id) {
        setCurrentMedication(discontinuedMed);
      }
      return discontinuedMed;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to discontinue medication order');
      console.error('Error discontinuing medication:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentMedication]);

  // Delete medication order
  const deleteMedication = useCallback(async (id: string) => {
    setSaving(true);
    setError(null);
    try {
      await medicationService.deleteMedicationOrder(id);
      setMedications((prev) => prev.filter((med) => med.id !== id));
      if (currentMedication?.id === id) {
        setCurrentMedication(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete medication order');
      console.error('Error deleting medication:', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [currentMedication]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      if (residentId) {
        fetchMedicationsByResident(residentId);
      } else {
        fetchMedications(0);
      }
    }
  }, [residentId, autoFetch, fetchMedications, fetchMedicationsByResident]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    fetchMedications(page);
  }, [fetchMedications]);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      fetchMedications(currentPage + 1);
    }
  }, [currentPage, totalPages, fetchMedications]);

  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      fetchMedications(currentPage - 1);
    }
  }, [currentPage, fetchMedications]);

  return {
    // State
    medications,
    currentMedication,
    loading,
    saving,
    error,
    totalElements,
    totalPages,
    currentPage,
    pageSize,

    // Actions
    fetchMedications,
    fetchMedicationsByResident,
    fetchActiveMedicationsByResident,
    fetchMedicationsByStatus,
    fetchMedicationById,
    createMedication,
    updateMedication,
    approveMedication,
    discontinueMedication,
    deleteMedication,
    handlePageChange,
    nextPage,
    prevPage,
    setCurrentMedication,
    setError,
  };
};
